// OP-3605 — Regras puras do cadastro do catálogo, espelho de `validarItemNovo`
// e `validarCategoriaNova` (domain/cadastro.ts). Gates visuais: a unicidade de
// verdade é do banco, que tem UNIQUE no código do item.
import { CategoriaRow, Controle, NovoItemPayload } from '../../../../types/estoque'
import { paraBusca } from '../formato'

/** Os tipos de demanda em que um item pode aparecer para quem solicita. */
export const TIPOS_DEMANDA_ITEM = ['INSTALACAO', 'ENVIO', 'PORTARIA'] as const

export interface FormItem {
  categoriaId: number | null
  codigo: string
  nome: string
  controle: Controle | ''
  unidadeMedida: string
  fatorCaixa: string
  tiposDemanda: string[]
  estoqueMinimo: string
  valorReposicao: number | null
}

export const ITEM_VAZIO: FormItem = {
  categoriaId: null, codigo: '', nome: '', controle: '', unidadeMedida: 'unidade',
  fatorCaixa: '', tiposDemanda: ['INSTALACAO', 'ENVIO', 'PORTARIA'], estoqueMinimo: '', valorReposicao: null,
}

/**
 * Espelho de `validarItemNovo`, na ordem do domínio, mais o que o controller
 * exige antes dele: categoria válida e números que sejam números.
 *
 * O regime (`controle`) é obrigatório aqui porque ele decide como o item é
 * controlado a vida inteira — e só pode mudar enquanto o item não tem peça,
 * saldo nem movimento. Errar agora custa caro depois.
 */
export function impedimentoNovoItem(f: FormItem, codigosEmUso: string[]): string | null {
  if (!f.categoriaId) return 'Escolha a categoria.'
  if (!f.codigo.trim()) return 'Informe o código.'
  if (!f.nome.trim()) return 'Informe o nome.'
  if (!f.controle) return 'Escolha como o item é controlado: por peça ou por quantidade.'
  if (codigosEmUso.map((c) => c.toUpperCase()).indexOf(f.codigo.trim().toUpperCase()) >= 0) {
    return 'Já existe um item com esse código.'
  }
  if (!f.tiposDemanda.length) {
    return 'Escolha ao menos um tipo de demanda: sem nenhum, o item não aparece para quem solicita.'
  }
  for (const [campo, valor] of [['fator de caixa', f.fatorCaixa], ['estoque mínimo', f.estoqueMinimo]] as const) {
    const v = valor.trim()
    // `^\d+$` já não admite sinal: um teste de negativo aqui seria inalcançável.
    if (v && !/^\d+$/.test(v)) return `O ${campo} precisa ser um número inteiro.`
  }
  return null
}

/** O payload de `createEstoqueItem`: o vazio vira ausente, não string vazia. */
export function montarNovoItem(f: FormItem): NovoItemPayload {
  const num = (v: string): number | null => (v.trim() === '' ? null : Number(v.trim().replace(',', '.')))
  return {
    categoriaId: Number(f.categoriaId),
    codigo: f.codigo.trim(),
    nome: f.nome.trim(),
    controle: f.controle as Controle,
    unidadeMedida: f.unidadeMedida.trim() || 'unidade',
    fatorCaixa: num(f.fatorCaixa),
    tiposDemanda: f.tiposDemanda.join(','),
    estoqueMinimo: num(f.estoqueMinimo),
    valorReposicao: f.valorReposicao,
  }
}

/**
 * Espelho de `validarCategoriaNova`: nome com pelo menos dois caracteres e
 * que não repita outra ativa. Duas categorias com o mesmo nome não quebram o
 * banco, quebram quem escolhe pelo nome.
 */
export function impedimentoNovaCategoria(nome: string, categorias: CategoriaRow[]): string | null {
  const n = nome.trim()
  if (n.length < 2) return 'Informe o nome da categoria.'
  if (categorias.some((c) => paraBusca(c.nome) === paraBusca(n))) {
    return 'Já existe uma categoria com esse nome.'
  }
  return null
}

export const rotuloCategoria = (c: Pick<CategoriaRow, 'nome' | 'paiNome'>): string => (c.paiNome ? `${c.paiNome} › ${c.nome}` : c.nome)
